<template>
  <div class="relative">
    <svg :width="size" :height="size" viewBox="0 0 100 100" class="transform -rotate-90">
      <!-- Первый сегмент (розовый) -->
      <path
        :d="femaleArcPath"
        fill="none"
        stroke="#F846C3"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
        class="transition-all duration-500 ease-in-out"
      />
      
      <!-- Закругления на концах первого сегмента -->
      <circle
        :cx="femaleStartPoint.x"
        :cy="femaleStartPoint.y"
        :r="strokeWidth / 2"
        fill="#F846C3"
        class="transition-all duration-500 ease-in-out"
      />
      <circle
        :cx="femaleEndPoint.x"
        :cy="femaleEndPoint.y"
        :r="strokeWidth / 2"
        fill="#F846C3"
        class="transition-all duration-500 ease-in-out"
      />

      <!-- Второй сегмент (синий) -->
      <path
        :d="maleArcPath"
        fill="none"
        stroke="#3B82F6"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
        class="transition-all duration-500 ease-in-out"
      />
      
      <!-- Закругления на концах второго сегмента -->
      <circle
        :cx="maleStartPoint.x"
        :cy="maleStartPoint.y"
        :r="strokeWidth / 2"
        fill="#3B82F6"
        class="transition-all duration-500 ease-in-out"
      />
      <circle
        :cx="maleEndPoint.x"
        :cy="maleEndPoint.y"
        :r="strokeWidth / 2"
        fill="#3B82F6"
        class="transition-all duration-500 ease-in-out"
      />
    </svg>

    <!-- Центральный текст (опционально) -->
    <div v-if="showCenterText" class="absolute inset-0 flex items-center justify-center">
      <div class="text-center">
        <div class="text-lg font-semibold text-txprimary">{{ totalPercent }}%</div>
        <div class="text-xs text-txprimary/60">Всего</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  interface Props {
    femalePercent: number;
    malePercent: number;
    size?: number;
    strokeWidth?: number;
    showCenterText?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    size: 80,
    strokeWidth: 8,
    showCenterText: false,
  });

  const radius = computed(() => (100 - props.strokeWidth) / 2);

  // Зазор в градусах между сегментами
  const gapDegrees = 14;

  // Конвертация процентов в углы с учетом зазоров
  const totalPercent = computed(() => props.femalePercent + props.malePercent);
  const availableDegrees = 360 - gapDegrees * 2;

  const femaleAngle = computed(() => (props.femalePercent / totalPercent.value) * availableDegrees);

  const maleAngle = computed(() => (props.malePercent / totalPercent.value) * availableDegrees);

  // Функция для создания SVG пути дуги
  const createArcPath = (startAngle: number, endAngle: number) => {
    const start = polarToCartesian(50, 50, radius.value, endAngle);
    const end = polarToCartesian(50, 50, radius.value, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M",
      start.x,
      start.y,
      "A",
      radius.value,
      radius.value,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
    ].join(" ");
  };

  const polarToCartesian = (
    centerX: number,
    centerY: number,
    radius: number,
    angleInDegrees: number
  ) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  const femaleArcPath = computed(() => createArcPath(0, femaleAngle.value));

  const maleArcPath = computed(() =>
    createArcPath(femaleAngle.value + gapDegrees, femaleAngle.value + gapDegrees + maleAngle.value)
  );

  // Координаты концов дуг для закруглений
  const femaleStartPoint = computed(() => polarToCartesian(50, 50, radius.value, femaleAngle.value));
  const femaleEndPoint = computed(() => polarToCartesian(50, 50, radius.value, 0));

  const maleStartPoint = computed(() =>
    polarToCartesian(50, 50, radius.value, femaleAngle.value + gapDegrees + maleAngle.value)
  );
  const maleEndPoint = computed(() => polarToCartesian(50, 50, radius.value, femaleAngle.value + gapDegrees));
</script>
